/**
 * useCart Hook
 *
 * This hook contains logic to handle what "cart" is active (e.g. "DEFAULT-COMICRELIEF", etc).
 * Each cart has distinct data related to financial attribute, money buys, etc.
 * This hook streamlines access to this cart data.
 *
 * consumers should use `useCartContext` from `./CartContext` instead of calling this hook all over the place
 *
 * This hook may be called from React components and hooks
 *
 * Use to access:
 * * Current active cartId - determined by site config or overridden with ?cardId= query string
 * * Current cart data - From cartsData loaded from payment service
 * * Safely retrieve cart properties:
 *   - getCartAvailableJourneys()
 *   - getCartDefaultJourney()
 *   - getCartJourneyHeader()
 *   - getCartJourneyMoneybuysTitle()
 *   - getCartJourneyMoneybuys()
 *   - getCartJourneySocialShare()
 *   - getCartJourneyCustomSuccessMessage()
 *   - getCartFormSubtitle()
 *   - getCartDefaultMoneybuy()
 *   - getCartFinancialAttribution()
 *   - getCartAbTest()
 *   - getCartCustomSuccessButton()
 *   - getCartBannerHtml()
 *   - getCartAdditionalLogo()
 */

import {
  useState, useEffect, useCallback
} from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  CartsByCartId,
  CartProps,
  CartJourneyOptions,
  Journey,
} from '@comicrelief/data-models/schemas/payments/models/Cart';

import { useAppSelector } from '@/store/hooks';
import { useSite } from '@/services/site/useSite';
import { useGetCartsDataQuery } from '@/api/carts/getCartsData';
import { UrlOverridesState } from '@/services/urlOverrides/urlOverridesSlice';

// Union type that includes all journey types, including 'direct' which is not part of the cart-based Journey type
type AllJourneyTypes = Journey | 'direct';

const loadCartData = (fullCartsData: CartsByCartId, cartId: string): CartProps | null => {
  if (!(cartId in fullCartsData)) {
    return null;
  }

  return fullCartsData[cartId];
};

const validateCartId = (fullCartsData: CartsByCartId, cartId: string): boolean => (
  // Check to see if cartId exists in cartsData?
  !!loadCartData(fullCartsData, cartId)
);

const getCartJourney = (selectedJourney: AllJourneyTypes | undefined, defaultJourney: string | null): Journey => {
  if (selectedJourney === 'direct') {
    return defaultJourney as Journey;
  }

  if (selectedJourney) {
    return selectedJourney;
  }

  return defaultJourney as Journey;
};

export function useCart() {
  const [searchParams] = useSearchParams();
  const environmentParam = searchParams.get('environment');

  const {
    data: cartsLoadData,
    error: cartsLoadError,
  } = useGetCartsDataQuery({
    environment: environmentParam ?? 'master'
  });

  const urlOverrides = useAppSelector(state => state.urlOverrides);
  const selectedJourney = useAppSelector(state => state.journeys.journey);

  const [cartId, setCartId] = useState<string>();
  const [cartData, setCartData] = useState<CartProps>();

  const { getSiteData } = useSite();

  const getDefaultCartId = useCallback((): string => {
    const { defaultCart } = getSiteData();
    return defaultCart;
  }, [getSiteData]);

  const determineCartId = useCallback((): string | null => {
    if (!cartsLoadData?.data) {
      return null;
    }

    if (urlOverrides.cartOverride && validateCartId(cartsLoadData.data, urlOverrides.cartOverride)) {
      return urlOverrides.cartOverride;
    }

    const defaultCartId = getDefaultCartId();
    if (defaultCartId && validateCartId(cartsLoadData.data, defaultCartId)) {
      return defaultCartId;
    }

    throw new Error('Cannot find valid cart');
  }, [cartsLoadData, urlOverrides.cartOverride, getDefaultCartId]);

  useEffect(() => {
    // Wait until cartsLoad API is complete
    if (!cartsLoadData?.data) {
      return;
    }

    // Determine cart ID based on cartOverride (if set in querystring) or default cart (from useSite)
    const newCartId = determineCartId();
    if (!newCartId) {
      return;
    }

    // Get cart data for cart ID
    const newCartData = loadCartData(cartsLoadData?.data, newCartId);
    if (!newCartData) {
      return;
    }

    // Set state
    setCartId(newCartId);
    setCartData(newCartData);
  }, [cartsLoadData, determineCartId, urlOverrides]);

  const getCartAvailableJourneys = useCallback((): Journey[] | null => {
    if (!cartData?.journeys) {
      return null;
    }

    return Object.keys(cartData.journeys) as Journey[];
  }, [cartData]);

  const getCartDefaultJourney = useCallback((): Journey | null => (
    cartData?.defaultJourney ?? null
  ), [cartData]);

  const getCartJourneyOptions = useCallback((): CartJourneyOptions | null => {
    const cartJourney = getCartJourney(selectedJourney, getCartDefaultJourney());

    return cartData?.journeys?.[cartJourney] ?? null;
  }, [cartData, selectedJourney, getCartDefaultJourney]);

  const getCartJourneyHeader = useCallback((): CartJourneyOptions['header'] | null => (
    getCartJourneyOptions()?.header ?? null
  ), [getCartJourneyOptions]);

  const getCartJourneyTitle = useCallback((): CartJourneyOptions['title'] | null => (
    getCartJourneyOptions()?.title ?? null
  ), [getCartJourneyOptions]);

  const getCartJourneySubtitle = useCallback((): CartJourneyOptions['subtitle'] | null => (
    getCartJourneyOptions()?.subtitle ?? null
  ), [getCartJourneyOptions]);

  const getCartJourneyMoneybuys = useCallback((): CartJourneyOptions['moneybuys'] | null => (
    getCartJourneyOptions()?.moneybuys ?? null
  ), [getCartJourneyOptions]);

  const getCartJourneySocialShare = useCallback((): CartJourneyOptions['socialShare'] | null => (
    getCartJourneyOptions()?.socialShare ?? null
  ), [getCartJourneyOptions]);

  const getCartJourneyCustomSuccessMessage = useCallback((): CartJourneyOptions['customSuccessMessage'] | null => (
    getCartJourneyOptions()?.customSuccessMessage ?? null
  ), [getCartJourneyOptions]);

  const getCartJourneyIntroCopy = useCallback((): CartJourneyOptions['introCopy'] | null => (
    getCartJourneyOptions()?.introCopy ?? null
  ), [getCartJourneyOptions]);

  const getCartDefaultMoneybuy = useCallback((): CartProps['defaultMoneybuy'] | null => (
    cartData?.defaultMoneybuy ?? null
  ), [cartData]);

  const getCartFinancialAttribution = useCallback((): CartProps['financialAttribution'] | null => (
    cartData?.financialAttribution ?? null
  ), [cartData]);

  const getCartAbTest = useCallback((): CartProps['abTest'] | null => (
    cartData?.abTest ?? null
  ), [cartData]);

  const getCartCustomSuccessButton = useCallback((): CartProps['customSuccessButton'] | null => (
    cartData?.customSuccessButton ?? null
  ), [cartData]);

  const getCartBannerHtml = useCallback((): CartProps['bannerCopy'] | null => (
    cartData?.bannerCopy ?? null
  ), [cartData]);

  const getCartAdditionalLogo = useCallback((): CartProps['additionalLogo'] | null => (
    cartData?.additionalLogo ?? null
  ), [cartData]);

  const getCartFooterCards = useCallback((): CartProps['footerCards'] | null => (
    cartData?.footerCards ?? null
  ), [cartData]);

  const getCartFulfilmentType = useCallback((): CartProps['fulfilmentType'] | null => (
    cartData?.fulfilmentType ?? null
  ), [cartData]);

  const getDesktopBackgroundColour = useCallback((): CartJourneyOptions['desktopBackgroundColour'] | null => (
    getCartJourneyOptions()?.desktopBackgroundColour ?? null
  ), [getCartJourneyOptions]);

  // Given that we ONLY forward the user from the CRcom Donate widget with these specific params in place
  // (they're never used in any other context), we can reliably use them as a flag here:
  const validateWidgetJourney = useCallback(
    (theseOverrides: UrlOverridesState | undefined) => Boolean(theseOverrides?.rowIdOverride && theseOverrides?.siteUrlOverride && theseOverrides?.givingTypeOverride && theseOverrides?.affiliateOverride && theseOverrides?.amountOverride),
    []
  );

  return {
    cartId,
    cartData,
    cartsLoadError,
    getCartAvailableJourneys,
    getCartDefaultJourney,
    getCartJourneyHeader,
    getCartJourneyTitle,
    getCartJourneySubtitle,
    getCartJourneyMoneybuys,
    getCartJourneySocialShare,
    getCartJourneyCustomSuccessMessage,
    getCartJourneyIntroCopy,
    getCartDefaultMoneybuy,
    getCartFinancialAttribution,
    getCartAbTest,
    getCartCustomSuccessButton,
    getCartBannerHtml,
    getCartAdditionalLogo,
    getCartFooterCards,
    getCartFulfilmentType,
    getDesktopBackgroundColour,
    validateWidgetJourney
  };
}
